import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { AnimatePresence, m } from "framer-motion";
import {
  AlertTriangle,
  BookOpen,
  Calendar,
  Check,
  Clock,
  Copy,
  Droplets,
  ExternalLink,
  FileJson2,
  FileText,
  Heart,
  Info,
  Link2,
  Loader2,
  Lock,
  Pencil,
  Phone,
  Pill,
  Share2,
  Shield,
  Stethoscope,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { showToast } from "@/lib/ui/toast";
import { handleVaultLocked, isVaultLockedResponse, reportVaultLocked } from "@/lib/vault-unlock";
import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";

import { AllergiesModal } from "./allergies-modal";
import { BloodTypeModal } from "./blood-type-modal";
import { EmergencyContactModal, type EmergencyContact } from "./emergency-contact-modal";
import { JournalTab } from "./journal-tab";
import { ProtectedContent, useVaultLock } from "./protected-content";
import { VaultMemberSwitcher } from "./vault-member-switcher";

export type VaultTab = "passport" | "journal" | "treatments" | "sharing";
export type TreatmentSubTab = "active" | "expired" | "all";

export const VAULT_TABS = ["passport", "journal", "treatments", "sharing"] as const;
export const TREATMENT_SUBTABS = ["active", "expired", "all"] as const;

interface MedicalVaultPageProps {
  activeTab: VaultTab;
  /** Family member whose journal is shown. Only the Journal is per-member —
   *  Passport, Treatments and Sharing always read the signed-in user's own
   *  record (apps/api account-health.ts), so the switcher lives in Journal. */
  activeMemberId: string | null;
  setActiveTab: (id: VaultTab) => void;
  setActiveMember: (id: string | null) => void;
  /**
   * The route's static page header renders outside the consent and PIN gates
   * (P32), so the title shows at once. The Lock button needs the gate's
   * context, so it stays here and is portalled into this header slot.
   */
  headerActionsSlot?: HTMLElement | null;
}

export function MedicalVaultPage({
  activeTab,
  activeMemberId,
  setActiveTab,
  setActiveMember,
  headerActionsSlot,
}: MedicalVaultPageProps) {
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  const tabs: { id: VaultTab; icon: React.ReactNode; label: React.ReactNode }[] = [
    { id: "passport", icon: <Shield className="size-4" />, label: <Trans>Passport</Trans> },
    { id: "journal", icon: <BookOpen className="size-4" />, label: <Trans>Journal</Trans> },
    { id: "treatments", icon: <Pill className="size-4" />, label: <Trans>Treatments</Trans> },
    { id: "sharing", icon: <Share2 className="size-4" />, label: <Trans>Sharing</Trans> },
  ];

  return (
    <ProtectedContent feature="medical_vault" fallback={<VaultBodySkeleton />}>
      <div>
        {/* Lock lives in the route's header row (portal); inline only when
            the page is rendered without one. */}
        {headerActionsSlot ? (
          createPortal(<VaultLockButton />, headerActionsSlot)
        ) : headerActionsSlot === undefined ? (
          <div className="flex justify-end">
            <VaultLockButton />
          </div>
        ) : null}

        {/* Tabs */}
        <TabBar
          tabs={tabs.map(
            (tab): TabItem<VaultTab> => ({
              id: tab.id,
              label: tab.label,
              icon: tab.icon,
            }),
          )}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
          className="mt-6"
        />

        {/* Tab content */}
        <SwipeableTabsContent
          ref={swipeRef}
          activeTab={activeTab}
          tabIds={VAULT_TABS}
          onTabChange={setActiveTab}
          className="mt-4"
          renderPanel={(id) => {
            if (id === "passport") return <PassportTab />;
            if (id === "journal") {
              // The member switcher scopes the Journal only — it sits inside
              // this panel so no other tab can look like a family member's.
              return (
                <div className="space-y-4">
                  <VaultMemberSwitcher
                    activeMemberId={activeMemberId}
                    onMemberChange={setActiveMember}
                  />
                  <JournalTab activeMemberId={activeMemberId} />
                </div>
              );
            }
            if (id === "treatments") return <TreatmentsTab />;
            if (id === "sharing") return <SharingTab />;
            return null;
          }}
        />
      </div>
    </ProtectedContent>
  );
}

/** What the vault looks like while the consent / PIN status loads: the tab
 *  row and the passport cards, in place of a blank screen (P32). */
export function VaultBodySkeleton() {
  return (
    <div aria-hidden>
      <div className="border-border mt-6 flex gap-6 border-b pb-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-5 w-20" />
        ))}
      </div>
      <div className="mt-4">
        <PassportSkeleton />
      </div>
    </div>
  );
}

function PassportSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="border-border bg-card animate-pulse rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="bg-muted size-10 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="bg-muted h-4 w-24 rounded" />
                <div className="bg-muted h-3 w-32 rounded" />
              </div>
            </div>
            <div className="bg-muted mt-3 h-11 w-full rounded-lg sm:pointer-fine:h-9" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Locks the vault now (shared device): the server's unlock ends (POST /lock)
 * and the PIN gate comes back. Shown only while a PIN-protected vault is open.
 */
function VaultLockButton() {
  const { canLock, lock } = useVaultLock();
  if (!canLock) return null;
  return (
    <Button variant="outline" size="touch" className="shrink-0" onClick={lock}>
      <Lock className="size-4" />
      <Trans>Lock</Trans>
    </Button>
  );
}

/* ─────────────────────── PASSPORT TAB ─────────────────────── */

interface PassportData {
  bloodType: string | null;
  allergies: string[];
  emergencyContacts: EmergencyContact[];
}

function PassportTab() {
  const { t } = useLingui();
  const [data, setData] = useState<PassportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [allergiesOpen, setAllergiesOpen] = useState(false);
  const [bloodTypeOpen, setBloodTypeOpen] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiFetch.get<PassportData>("/api/account/medical-passport");
      setData(result);
    } catch (err) {
      // Locked meanwhile: the PIN gate comes back (and this refetches after).
      // Anything else: show the error card. Never fall through to the empty
      // editors — saving from them would replace the stored allergies or
      // contacts with an empty list.
      handleVaultLocked(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveField = useCallback(
    async (field: Partial<PassportData>) => {
      // The PUT replaces whole fields, so only save on top of a successful load.
      if (!data) throw new Error("Passport not loaded");
      const toast = showToast(t`Saving…`, "loading");
      try {
        await apiFetch.put("/api/account/medical-passport", field);
        setData((prev) => (prev ? { ...prev, ...field } : prev));
        toast.update(t`Saved`, "success");
      } catch (err) {
        if (handleVaultLocked(err)) {
          toast.update(t`Unlock your medical vault to continue.`, "info");
        } else {
          toast.update(t`Could not save. Try again.`, "error");
        }
        throw new Error("Save failed");
      }
    },
    [t, data],
  );

  if (loading) return <PassportSkeleton />;

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="border-border bg-card flex flex-col items-center gap-3 rounded-xl border px-5 py-10 text-center">
          <AlertTriangle className="text-warning-strong size-8" aria-hidden />
          <p className="text-foreground text-sm font-medium">
            <Trans>Couldn't load your medical passport.</Trans>
          </p>
          <p className="text-muted-foreground max-w-sm text-xs">
            <Trans>Your saved data is unchanged. Editing is turned off until it loads.</Trans>
          </p>
          <Button variant="outline" className="min-h-11" onClick={() => void fetchData()}>
            <Trans>Retry</Trans>
          </Button>
        </div>
        <AccountSecurityBanner />
      </div>
    );
  }

  const allergies = data.allergies ?? [];
  const bloodType = data.bloodType ?? null;
  const emergencyContacts = data.emergencyContacts ?? [];

  return (
    <div className="space-y-4">
      {/* Emergency info cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Allergies */}
        <div className="border-border bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400">
              <AlertTriangle className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground text-sm font-semibold">
                <Trans>Allergies</Trans>
              </p>
              {allergies.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {allergies.map((a) => (
                    <span
                      key={a}
                      className="inline-block rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-300"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground mt-0.5 text-xs">
                  <Trans>No allergies on file</Trans>
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="touch"
            className="mt-3 w-full"
            onClick={() => setAllergiesOpen(true)}
          >
            {allergies.length > 0 ? <Pencil className="size-4" aria-hidden /> : null}
            {allergies.length > 0 ? <Trans>Edit allergies</Trans> : <Trans>Add allergies</Trans>}
          </Button>
        </div>

        {/* Blood type */}
        <div className="border-border bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400">
              <Droplets className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground text-sm font-semibold">
                <Trans>Blood type</Trans>
              </p>
              {bloodType ? (
                <p className="text-foreground mt-0.5 text-lg font-bold">{bloodType}</p>
              ) : (
                <p className="text-muted-foreground mt-0.5 text-xs">
                  <Trans>Not set</Trans>
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="touch"
            className="mt-3 w-full"
            onClick={() => setBloodTypeOpen(true)}
          >
            {bloodType ? <Pencil className="size-4" aria-hidden /> : null}
            {bloodType ? <Trans>Edit blood type</Trans> : <Trans>Set blood type</Trans>}
          </Button>
        </div>

        {/* Emergency contact */}
        <div className="border-border bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400">
              <Phone className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground text-sm font-semibold">
                <Trans>Emergency contact</Trans>
              </p>
              {emergencyContacts.length > 0 ? (
                <div className="mt-1 space-y-0.5">
                  {emergencyContacts.map((c, i) => (
                    <p key={i} className="text-foreground text-xs">
                      {c.name}
                      {c.relationship && (
                        <span className="text-muted-foreground"> ({c.relationship})</span>
                      )}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground mt-0.5 text-xs">
                  <Trans>No contact set</Trans>
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="touch"
            className="mt-3 w-full"
            onClick={() => setEmergencyOpen(true)}
          >
            {emergencyContacts.length > 0 ? <Pencil className="size-4" aria-hidden /> : null}
            {emergencyContacts.length > 0 ? (
              <Trans>Edit emergency contact</Trans>
            ) : (
              <Trans>Add emergency contact</Trans>
            )}
          </Button>
        </div>
      </div>

      {/* Chronic conditions + current medication have no data source or
          editor yet — shown as "coming soon", never as an empty record. */}
      <div className="grid gap-4 sm:grid-cols-2">
        <ComingSoonCard
          icon={Heart}
          title={<Trans>Chronic conditions</Trans>}
          description={<Trans>Conditions doctors should know about.</Trans>}
        />
        <ComingSoonCard
          icon={Pill}
          title={<Trans>Current medication</Trans>}
          description={<Trans>Your active prescriptions, in one place.</Trans>}
        />
      </div>

      <AccountSecurityBanner />

      {/* Modals */}
      <AllergiesModal
        open={allergiesOpen}
        onOpenChange={setAllergiesOpen}
        currentAllergies={allergies}
        onSave={(vals) => saveField({ allergies: vals })}
      />
      <BloodTypeModal
        open={bloodTypeOpen}
        onOpenChange={setBloodTypeOpen}
        currentValue={bloodType}
        onSave={(val) => saveField({ bloodType: val })}
      />
      <EmergencyContactModal
        open={emergencyOpen}
        onOpenChange={setEmergencyOpen}
        currentContacts={emergencyContacts}
        onSave={(vals) => saveField({ emergencyContacts: vals })}
      />
    </div>
  );
}

/* ─────────────────────── TREATMENTS TAB ─────────────────────── */

/**
 * Treatments (prescriptions, schedules, refill reminders) is not built yet.
 * One honest "coming soon" card instead of three identical empty subtabs;
 * the `?subtab=` URL param is still accepted by the route and simply ignored.
 */
function TreatmentsTab() {
  return (
    <div className="space-y-4">
      <ComingSoonCard
        icon={Pill}
        title={<Trans>Treatments</Trans>}
        description={
          <Trans>Active prescriptions, schedules and refill reminders will appear here.</Trans>
        }
      />
      <AccountSecurityBanner />
    </div>
  );
}

function ComingSoonCard({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: React.ReactNode;
  description: React.ReactNode;
}) {
  return (
    <div className="border-border bg-card rounded-xl border p-5">
      <div className="flex items-start gap-3">
        <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-lg">
          <Icon className="size-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-foreground text-sm font-semibold">{title}</p>
            <Badge variant="neutral">
              <Trans>Coming soon</Trans>
            </Badge>
          </div>
          <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── SHARING TAB ─────────────────────── */

function SharingTab() {
  const { t } = useLingui();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [generatingShare, setGeneratingShare] = useState(false);
  const [downloadingJson, setDownloadingJson] = useState(false);
  const [downloadingHtml, setDownloadingHtml] = useState(false);

  const handleDownloadJson = useCallback(async () => {
    setDownloadingJson(true);
    try {
      const data = await apiFetch.get<unknown>("/api/account/health-passport?format=json");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "health-passport.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (!handleVaultLocked(err)) showToast(t`Download failed. Try again.`, "error");
    } finally {
      setDownloadingJson(false);
    }
  }, [t]);

  const handleDownloadHtml = useCallback(async () => {
    setDownloadingHtml(true);
    try {
      const res = await apiFetch.raw("/api/account/health-passport?format=html", { method: "GET" });
      if (await isVaultLockedResponse(res)) {
        reportVaultLocked();
        return;
      }
      if (!res.ok) throw new Error("Failed");
      const html = await res.text();
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "health-passport.html";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      showToast(t`Download failed. Try again.`, "error");
    } finally {
      setDownloadingHtml(false);
    }
  }, [t]);

  const handleGenerateShare = useCallback(async () => {
    setGeneratingShare(true);
    try {
      const data = await apiFetch.post<{ shareUrl: string }>(
        "/api/account/health-passport/share",
        {},
      );
      setShareUrl(data.shareUrl);
    } catch (err) {
      if (!handleVaultLocked(err)) showToast(t`Could not generate link. Try again.`, "error");
    } finally {
      setGeneratingShare(false);
    }
  }, [t]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent — clipboard unavailable.
    }
  }, [shareUrl]);

  return (
    <div className="space-y-4">
      {/* "Who has access" is hidden until persistent share grants exist —
          the only sharing today is the 24-hour link below. */}
      {/* Generate share link */}
      <div className="border-border bg-card rounded-xl border p-5">
        <h2 className="text-foreground flex items-center gap-2 text-base font-semibold">
          <Link2 className="text-primary size-5" />
          <Trans>Temporary link</Trans>
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Generate a 24-hour read-only link to share with a doctor.</Trans>
        </p>

        <div className="mt-4">
          <AnimatePresence mode="wait">
            {!shareUrl ? (
              <m.div
                key="gen"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Button
                  onClick={handleGenerateShare}
                  disabled={generatingShare}
                  className="min-h-[44px] gap-2"
                >
                  {generatingShare ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Link2 className="size-4" />
                  )}
                  <Trans>Generate link</Trans>
                </Button>
              </m.div>
            ) : (
              <m.div
                key="result"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="bg-primary/5 border-primary/20 rounded-lg border p-3">
                  <div className="flex items-center gap-2">
                    <Check className="size-4 text-emerald-500" />
                    <span className="text-foreground text-sm font-medium">
                      <Trans>Link generated</Trans>
                    </span>
                  </div>
                  <div className="bg-background border-border mt-2 flex items-center gap-2 rounded-md border p-2">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl}
                      className="text-foreground min-w-0 flex-1 bg-transparent text-xs outline-none"
                    />
                    <Button
                      variant="ghost"
                      size="touch"
                      className="shrink-0 px-2"
                      onClick={handleCopy}
                    >
                      {copied ? (
                        <>
                          <Check className="size-3.5 text-emerald-500" />
                          <span className="text-xs">
                            <Trans>Copied</Trans>
                          </span>
                        </>
                      ) : (
                        <>
                          <Copy className="size-3.5" />
                          <span className="text-xs">
                            <Trans>Copy</Trans>
                          </span>
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="text-muted-foreground mt-2 flex items-center gap-1.5 text-xs">
                    <Clock className="size-3" />
                    <Trans>Expires in 24 hours</Trans>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] gap-1.5"
                  onClick={() => window.open(shareUrl, "_blank")}
                >
                  <ExternalLink className="size-3.5" />
                  <Trans>Preview</Trans>
                </Button>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Download / Export */}
      <div className="border-border bg-card rounded-xl border p-5">
        <h2 className="text-foreground flex items-center gap-2 text-base font-semibold">
          <FileText className="text-primary size-5" />
          <Trans>Export data</Trans>
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Download your data in standard medical formats.</Trans>
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Button
            variant="outline"
            className="h-auto min-h-[44px] justify-start gap-3 px-4 py-3"
            onClick={handleDownloadJson}
            disabled={downloadingJson}
          >
            {downloadingJson ? (
              <Loader2 className="text-primary size-5 animate-spin" />
            ) : (
              <FileJson2 className="text-primary size-5" />
            )}
            <div className="text-left">
              <div className="text-foreground text-sm font-medium">
                <Trans>Download JSON</Trans>
              </div>
              <div className="text-muted-foreground text-xs">FHIR R4</div>
            </div>
          </Button>
          <Button
            variant="outline"
            className="h-auto min-h-[44px] justify-start gap-3 px-4 py-3"
            onClick={handleDownloadHtml}
            disabled={downloadingHtml}
          >
            {downloadingHtml ? (
              <Loader2 className="text-primary size-5 animate-spin" />
            ) : (
              <FileText className="size-5 text-indigo-500" />
            )}
            <div className="text-left">
              <div className="text-foreground text-sm font-medium">
                <Trans>Download HTML</Trans>
              </div>
              <div className="text-muted-foreground text-xs">
                <Trans>Print-ready</Trans>
              </div>
            </div>
          </Button>
        </div>
      </div>

      {/* What's included */}
      <div className="border-border bg-card rounded-xl border p-5">
        <h2 className="text-foreground flex items-center gap-2 text-base font-semibold">
          <Info className="text-primary size-5" />
          <Trans>What's included</Trans>
        </h2>
        <ul className="mt-3 space-y-2.5">
          <li className="flex items-start gap-3">
            <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400">
              <Calendar className="size-4" />
            </div>
            <p className="text-foreground text-sm font-medium">
              <Trans>Past visits and appointments</Trans>
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400">
              <Stethoscope className="size-4" />
            </div>
            <p className="text-foreground text-sm font-medium">
              <Trans>Providers seen</Trans>
            </p>
          </li>
        </ul>
      </div>

      {/* Privacy */}
      <div className="bg-muted/50 border-border rounded-xl border p-4">
        <div className="flex items-start gap-3">
          <Shield className="text-primary mt-0.5 size-5 shrink-0" />
          <p className="text-muted-foreground text-sm">
            <Trans>
              Your medical data is encrypted at rest and in transit. Only you control who can access
              it and for how long.
            </Trans>
          </p>
        </div>
      </div>

      <AccountSecurityBanner />
    </div>
  );
}
